import { useCallback, useEffect, useRef, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { WRITE_ROLES, decodeRoles, initializeSession } from '../../lib/auth'
import type { Lang } from '../../lib/i18n'
import { createOrder, HttpError, OrderCreateError, type CreateOrderInput, type StaffOrderChannel } from '../../lib/orders-api'
import {
  findCustomersByPhone,
  hopFindOrCreateCustomer,
  searchCustomersByPhone,
  searchProducts,
  fetchDeliveryZones,
  type CustomerSearchResult,
  type SearchedCustomer,
  type LookupProduct,
  type DeliveryZone,
} from '../../lib/intake-api'
import { useRouteDraft } from '../../lib/route-draft'
import { CommandHeader } from '../../components/CommandHeader'
import { Button } from '../../components/ui/button'
import { formatMoney } from './format'
import { formatLebanesePhone, nationalDigitCount, normalizeLebanesePhone } from './lebanese-phone'
import { ot } from './orders-i18n'

// R487: the product box queries as you type, after this pause with no keystroke. R485: the
// phone box uses the same pause.
export const SEARCH_DEBOUNCE_MS = 250
// R487: the product box fires from this many characters (one letter matches most of the catalogue).
export const PRODUCT_SEARCH_MIN_CHARS = 2
// R485 (Rook #66468): the phone box lists customers from this many national digits.
export const PHONE_SEARCH_MIN_DIGITS = 2
// R485: "no customer with this number, create" is offered only for a number long enough to be
// whole (a Lebanese national number is 7 or 8 digits); a shorter prefix just lists nothing.
const PHONE_CREATE_MIN_DIGITS = 7

interface Props {
  lang: Lang
  onLangToggle: () => void
  isOnline: boolean
}

interface ChosenCustomer {
  id: string
  name: string | null
  lastZoneId: string | null
}

// R492 (Rook #66472, Frank #66477): the form is a route draft in sessionStorage, restored when
// the person comes back to /orders/new and cleared when the order is created (and on sign-out,
// shell-clear.ts). The customer-create half (phone and name) is its own scope, 'customer'.
// Rook #66697: the hand-entered form offers SALE and COMP only (the DTO also takes LEGACY, which
// is the importer's, never typed).
type StaffOrderType = 'SALE' | 'COMP'

interface FormDraft {
  submitKey: string
  customer: ChosenCustomer | null
  items: ItemLine[]
  notes: string
  channel: '' | StaffOrderChannel
  orderType: StaffOrderType
  fulfillmentMethod: '' | 'delivery' | 'pickup'
  deliveryZoneId: string
  deliveryLocationId: string
  streetName: string
  deliveryNotes: string
}

interface CustomerDraft {
  phone: string
  name: string
}

// Frank #66570: in Arabic a product with no Arabic name falls back to its English name, then
// its slug; English reads nameEn, then slug.
function productName(product: { id: string; nameEn?: string; nameAr?: string; slug?: string }, lang: Lang): string {
  return (lang === 'ar' ? product.nameAr ?? product.nameEn : product.nameEn) ?? product.slug ?? product.id
}

// R487: a line exists only once a product was tapped, so it always carries its product.
interface ItemLine {
  key: number
  product: LookupProduct
  quantity: string
  variantId: string
}

type FieldErrors = Record<string, string>

function validQuantity(raw: string): number | null {
  const qty = Number(raw)
  return Number.isInteger(qty) && qty >= 1 && qty <= 1000 ? qty : null
}

// R488: the same price data the create sends against (the lookup's product.price, the zone list's
// location.deliveryFee; the server re-reads both from the database). A line with an invalid
// quantity adds nothing until it is fixed; the submit refuses it anyway.
function linesSubtotal(items: ItemLine[]): number {
  const cents = items.reduce((sum, line) => {
    const qty = validQuantity(line.quantity)
    const price = Number(line.product.price)
    return qty !== null && Number.isFinite(price) ? sum + Math.round(price * 100) * qty : sum
  }, 0)
  return cents / 100
}

function mapLookupError(err: unknown, lang: Lang): string {
  if (err instanceof HttpError) {
    if (err.status === 400) return ot(lang, 'newOrderPhoneInvalid')
    if (err.status === 403) return ot(lang, 'newOrderLookupForbidden')
    if (err.status === 429) return ot(lang, 'newOrderCustomerRateLimited')
  }
  return ot(lang, 'newOrderLookupFailed')
}

export function NewOrderScreen({ lang, onLangToggle, isOnline }: Props) {
  const navigate = useNavigate()
  const [authState, setAuthState] = useState<'checking' | 'ok' | 'redirect'>('checking')

  // R115: one key per form, sent on every submit of it, so a double tap or a retry is one order.
  // R492: the draft is read once, when the screen mounts; every field below starts from it.
  const { draft: formDraft, save: saveFormDraft, clear: clearFormDraft } = useRouteDraft<FormDraft>('form')
  const { draft: customerDraft, save: saveCustomerDraft, clear: clearCustomerDraft } = useRouteDraft<CustomerDraft>('customer')
  const [restored] = useState(() => ({ form: formDraft, customer: customerDraft }))
  const submitted = useRef(false)
  // The key travels with the draft, so a restored form retried after a lost answer is one order.
  const [submitKey] = useState(() => restored.form?.submitKey ?? crypto.randomUUID())
  const [customer, setCustomer] = useState<ChosenCustomer | null>(restored.form?.customer ?? null)
  const [customerPhone, setCustomerPhone] = useState(restored.customer?.phone ?? '')
  const [customerName, setCustomerName] = useState(restored.customer?.name ?? '')
  const [customerSearch, setCustomerSearch] = useState<CustomerSearchResult | null>(null)
  const [customerSearching, setCustomerSearching] = useState(false)
  const [customerError, setCustomerError] = useState<string | null>(null)
  // R485: one pending timer and one request number for the phone box, as the product box has.
  const customerTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const customerSeq = useRef(0)
  const [items, setItems] = useState<ItemLine[]>(restored.form?.items ?? [])
  const [productQuery, setProductQuery] = useState('')
  const [productResults, setProductResults] = useState<LookupProduct[] | null>(null)
  const [productSearching, setProductSearching] = useState(false)
  const [productError, setProductError] = useState<string | null>(null)
  // R487: one pending timer and one request number for the product box; a response that is not
  // the latest request's is dropped, so a slow early answer never overwrites a later one.
  const productTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const productSeq = useRef(0)
  const lineKey = useRef((restored.form?.items ?? []).reduce((max, line) => Math.max(max, line.key), 0))
  const productBoxRef = useRef<HTMLInputElement | null>(null)
  const [notes, setNotes] = useState(restored.form?.notes ?? '')
  const [channel, setChannel] = useState<'' | StaffOrderChannel>(restored.form?.channel ?? '')
  // R489 (Rook #66589, #66697): source order id, currency and FX rate id are off the form (the
  // server keeps its defaults, orders.service.ts: currency USD). The order type stays, SALE or
  // COMP only (COMP is a kind the till knows), SALE by default; LEGACY is not offered.
  const [orderType, setOrderType] = useState<StaffOrderType>(restored.form?.orderType === 'COMP' ? 'COMP' : 'SALE')
  const [fulfillmentMethod, setFulfillmentMethod] = useState<'' | 'delivery' | 'pickup'>(restored.form?.fulfillmentMethod ?? '')
  const [zones, setZones] = useState<DeliveryZone[] | null>(null)
  const zonesRequested = useRef(false)
  const [deliveryZoneId, setDeliveryZoneId] = useState(restored.form?.deliveryZoneId ?? '')
  const [deliveryLocationId, setDeliveryLocationId] = useState(restored.form?.deliveryLocationId ?? '')
  const [streetName, setStreetName] = useState(restored.form?.streetName ?? '')
  const [deliveryNotes, setDeliveryNotes] = useState(restored.form?.deliveryNotes ?? '')

  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  useEffect(() => {
    initializeSession().then(() => {
      const r = decodeRoles()
      setAuthState(r.some((role) => (WRITE_ROLES as readonly string[]).includes(role)) ? 'ok' : 'redirect')
    })
  }, [])

  // z4: once zones arrive and the chosen customer has a lastZoneId in that list,
  // start the zone select on it — but never override a zone the operator already picked.
  useEffect(() => {
    if (!zones || !customer?.lastZoneId || deliveryZoneId) return
    if (zones.some((z) => z.id === customer.lastZoneId)) {
      setDeliveryZoneId(customer.lastZoneId)
    }
  }, [zones, customer, deliveryZoneId])

  useEffect(() => () => {
    if (productTimer.current) clearTimeout(productTimer.current)
    if (customerTimer.current) clearTimeout(customerTimer.current)
  }, [])

  // R492: every change is the draft; nothing is written after the order was created.
  useEffect(() => {
    if (submitted.current) return
    saveFormDraft({
      submitKey, customer, items, notes, channel, orderType, fulfillmentMethod,
      deliveryZoneId, deliveryLocationId, streetName, deliveryNotes,
    })
  }, [saveFormDraft, submitKey, customer, items, notes, channel, orderType, fulfillmentMethod, deliveryZoneId, deliveryLocationId, streetName, deliveryNotes])

  useEffect(() => {
    if (submitted.current) return
    saveCustomerDraft({ phone: customerPhone, name: customerName })
  }, [saveCustomerDraft, customerPhone, customerName])

  const updateItem = useCallback((index: number, patch: Partial<ItemLine>) => {
    setItems((rows) => rows.map((row, i) => (i === index ? { ...row, ...patch } : row)))
  }, [])

  const removeItem = useCallback((index: number) => {
    setItems((rows) => rows.filter((_, i) => i !== index))
  }, [])

  const runCustomerSearch = useCallback(async (phone: string) => {
    const mine = ++customerSeq.current
    setCustomerError(null)
    setCustomerSearching(true)
    try {
      const found = await searchCustomersByPhone(phone)
      if (mine === customerSeq.current) setCustomerSearch(found)
    } catch (err) {
      if (mine === customerSeq.current) setCustomerError(mapLookupError(err, lang))
    } finally {
      if (mine === customerSeq.current) setCustomerSearching(false)
    }
  }, [lang])

  // R485 (Rook #66468): the phone box lists customers as you type, from 2 national digits, after
  // the pause; every keystroke drops the list on screen and any answer still on its way.
  const scheduleCustomerSearch = useCallback((value: string) => {
    setCustomerSearch(null)
    setCustomerError(null)
    setCustomerSearching(false)
    customerSeq.current += 1
    if (customerTimer.current) clearTimeout(customerTimer.current)
    customerTimer.current = null
    if (nationalDigitCount(value) < PHONE_SEARCH_MIN_DIGITS || !isOnline) return
    const phone = value.trim()
    customerTimer.current = setTimeout(() => {
      customerTimer.current = null
      void runCustomerSearch(phone)
    }, SEARCH_DEBOUNCE_MS)
  }, [isOnline, runCustomerSearch])

  const handleCustomerPhoneChange = useCallback((value: string) => {
    setCustomerPhone(value)
    scheduleCustomerSearch(value)
  }, [scheduleCustomerSearch])

  // A restored phone box (R492) lists again once the session is known, as if just typed.
  const restoredSearchRan = useRef(false)
  useEffect(() => {
    if (authState !== 'ok' || restoredSearchRan.current) return
    restoredSearchRan.current = true
    if (!customer && customerPhone) scheduleCustomerSearch(customerPhone)
  }, [authState, customer, customerPhone, scheduleCustomerSearch])

  const handleCustomerCreate = useCallback(async () => {
    setCustomerError(null)
    try {
      const created = await hopFindOrCreateCustomer({ phone: customerPhone, name: customerName })
      setCustomer({ id: created.id, name: created.name, lastZoneId: created.lastZoneId })
      setCustomerSearch(null)
      setCustomerName('')
    } catch (err) {
      if (err instanceof HttpError && err.status === 400 && customerName.trim().length === 1) {
        setCustomerError(ot(lang, 'newOrderNameTooShort'))
      } else {
        setCustomerError(mapLookupError(err, lang))
      }
    }
  }, [customerPhone, customerName, lang])

  // R485: a tap picks the row. The search answers the picker set only (id, name, phone), so the
  // customer's last delivery zone (z4) is read from the exact by-phone lookup on the picked
  // row's stored number; a failure there only means no zone is pre-chosen.
  const pickCustomer = useCallback((found: SearchedCustomer) => {
    if (customerTimer.current) clearTimeout(customerTimer.current)
    customerTimer.current = null
    customerSeq.current += 1
    setCustomer({ id: found.id, name: found.name, lastZoneId: null })
    setCustomerSearch(null)
    setCustomerSearching(false)
    setCustomerError(null)
    if (!found.phone) return
    findCustomersByPhone(found.phone)
      .then((list) => {
        const zoneId = list.find((c) => c.id === found.id)?.lastZoneId ?? null
        if (zoneId) setCustomer((prev) => (prev && prev.id === found.id ? { ...prev, lastZoneId: zoneId } : prev))
      })
      .catch(() => {})
  }, [])

  const handleCustomerChange = useCallback(() => {
    if (customerTimer.current) clearTimeout(customerTimer.current)
    customerTimer.current = null
    customerSeq.current += 1
    setCustomer(null)
    setCustomerSearch(null)
    setCustomerSearching(false)
    setCustomerPhone('')
    setCustomerName('')
    setCustomerError(null)
  }, [])

  const runProductSearch = useCallback(async (query: string) => {
    const mine = ++productSeq.current
    setProductError(null)
    setProductSearching(true)
    try {
      const found = await searchProducts(query)
      if (mine === productSeq.current) setProductResults(found.slice(0, 20))
    } catch {
      if (mine === productSeq.current) setProductError(ot(lang, 'newOrderLookupFailed'))
    } finally {
      if (mine === productSeq.current) setProductSearching(false)
    }
  }, [lang])

  // R487: the product box searches as you type, like the storefront's box (no Search button).
  const handleProductQueryChange = useCallback((value: string) => {
    setProductQuery(value)
    setProductResults(null)
    setProductError(null)
    setProductSearching(false)
    productSeq.current += 1
    if (productTimer.current) clearTimeout(productTimer.current)
    productTimer.current = null
    const query = value.trim()
    if (query.length < PRODUCT_SEARCH_MIN_CHARS || !isOnline) return
    productTimer.current = setTimeout(() => {
      productTimer.current = null
      void runProductSearch(query)
    }, SEARCH_DEBOUNCE_MS)
  }, [isOnline, runProductSearch])

  // R487: a tap adds the line and clears the box, ready for the next product.
  // Rook #66451: a tap on a product already on the form raises that line's quantity instead.
  // "Already on the form" is the same product id AND the same variant id: the one variant's id
  // for a single-variant product (sent with the line), the chosen id for a multi-variant one
  // (empty until chosen, so a second tap before the choice raises the unchosen line), empty for
  // a product with no variants.
  const pickProduct = useCallback((product: LookupProduct) => {
    const variantId = product.variants.length === 1 ? product.variants[0].id : ''
    lineKey.current += 1
    const key = lineKey.current
    setItems((rows) => {
      const same = rows.findIndex((row) => row.product.id === product.id && row.variantId === variantId)
      if (same === -1) return [...rows, { key, product, quantity: '1', variantId }]
      return rows.map((row, i) => (i === same ? { ...row, quantity: String(Math.min((validQuantity(row.quantity) ?? 0) + 1, 1000)) } : row))
    })
    if (productTimer.current) clearTimeout(productTimer.current)
    productTimer.current = null
    productSeq.current += 1
    setProductQuery('')
    setProductResults(null)
    setProductSearching(false)
    setProductError(null)
    productBoxRef.current?.focus()
  }, [])

  const loadZones = useCallback(() => {
    if (zonesRequested.current) return
    zonesRequested.current = true
    fetchDeliveryZones()
      .then((list) => setZones(list))
      .catch(() => {
        zonesRequested.current = false
        setFieldErrors((prev) => ({ ...prev, zone: ot(lang, 'newOrderLookupFailed') }))
      })
  }, [lang])

  const handleFulfillmentChange = useCallback((value: '' | 'delivery' | 'pickup') => {
    setFulfillmentMethod(value)
    if (value === 'delivery' && zones === null) loadZones()
  }, [zones, loadZones])

  // A restored delivery (R492) needs its zone list to show the chosen zone and area.
  useEffect(() => {
    if (authState === 'ok' && fulfillmentMethod === 'delivery' && zones === null) loadZones()
  }, [authState, fulfillmentMethod, zones, loadZones])

  const handleZoneChange = useCallback((value: string) => {
    setDeliveryZoneId(value)
    setDeliveryLocationId('')
  }, [])

  const validate = useCallback((): FieldErrors => {
    const errors: FieldErrors = {}
    if (!customer) errors.customerId = ot(lang, 'newOrderCustomerRequired')
    if (!channel) errors.channel = ot(lang, 'newOrderChannelRequired')
    if (items.length === 0) errors.items = ot(lang, 'newOrderItemsMin')
    items.forEach((item, i) => {
      if (validQuantity(item.quantity) === null) {
        errors[`item-${i}-quantity`] = ot(lang, 'newOrderItemQuantityInvalid')
      }
      // Rook #66451: a product with real variants needs a real choice.
      if (item.product.variants.length > 1 && !item.variantId) {
        errors[`item-${i}-variant`] = ot(lang, 'newOrderVariantRequired')
      }
    })
    if (fulfillmentMethod === '') errors.fulfillmentMethod = ot(lang, 'newOrderFulfillmentRequired')
    if (fulfillmentMethod === 'delivery' && (!deliveryZoneId || !deliveryLocationId)) {
      errors.zone = ot(lang, 'newOrderZoneRequired')
    }
    return errors
  }, [customer, channel, items, fulfillmentMethod, deliveryZoneId, deliveryLocationId, lang])

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      const errors = validate()
      setFieldErrors(errors)
      if (Object.keys(errors).length > 0) return

      if (!channel) return // validate() already set the field error; satisfies the type below

      setSubmitting(true)
      setSubmitError(null)
      const payload: CreateOrderInput = {
        customerId: customer?.id ?? '',
        channel,
        orderType,
        idempotencyKey: submitKey,
        items: items.map((item) => ({
          productId: item.product.id,
          quantity: Number(item.quantity),
          ...(item.variantId.trim() ? { variantId: item.variantId.trim() } : {}),
        })),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
        ...(fulfillmentMethod === 'pickup' ? { fulfillmentMethod: 'pickup' as const } : {}),
        ...(fulfillmentMethod === 'delivery'
          ? { fulfillmentMethod: 'delivery' as const, deliveryZoneId, deliveryLocationId }
          : {}),
        ...(fulfillmentMethod === 'delivery' && streetName.trim() ? { streetName: streetName.trim() } : {}),
        ...(fulfillmentMethod === 'delivery' && deliveryNotes.trim() ? { deliveryNotes: deliveryNotes.trim() } : {}),
      }

      try {
        const created = await createOrder(payload)
        // R492: the order exists, so the form's drafts go (both scopes) before leaving.
        submitted.current = true
        clearFormDraft()
        clearCustomerDraft()
        // Navigate to the canonical GET :id read rather than trusting the create
        // response's shape — OrderDetailScreen's own fetchOrder is the single source
        // of truth for status/data, so the operator always sees what the server holds.
        navigate(`/orders/${created.id}`, { replace: true })
      } catch (err) {
        setSubmitError(err instanceof OrderCreateError ? err.message : ot(lang, 'newOrderSubmitErrorGeneric'))
        setSubmitting(false)
      }
    },
    [
      validate, customer, items, notes, channel, orderType, submitKey,
      fulfillmentMethod, deliveryZoneId, deliveryLocationId,
      streetName, deliveryNotes, clearFormDraft, clearCustomerDraft,
      navigate, lang,
    ],
  )

  if (authState === 'checking') return null
  if (authState === 'redirect') return <Navigate to="/login" replace />

  const dir = lang === 'ar' ? 'rtl' : 'ltr'
  const inputClass =
    'w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900'

  const productQueryTrimmed = productQuery.trim()

  // R488: the running total, from the lines and the chosen area's fee. Pickup has no fee; a
  // delivery with no area yet shows the lines alone and says the fee is still to come.
  // R489: no currency on the form; the server creates in USD (orders.service.ts, dto.currency ?? 'USD').
  const money = (amount: number) => formatMoney(amount, 'USD')
  const phoneDigits = nationalDigitCount(customerPhone)
  const phoneReadsAs = normalizeLebanesePhone(customerPhone)
  const customerItems = customerSearch?.items ?? []
  const customerMore = customerSearch ? customerSearch.total - customerSearch.items.length : 0
  const offerCreate = !!customerSearch && customerSearch.total === 0 && phoneDigits >= PHONE_CREATE_MIN_DIGITS
  const subtotal = linesSubtotal(items)
  const chosenLocation = fulfillmentMethod === 'delivery'
    ? (zones ?? []).find((zone) => zone.id === deliveryZoneId)?.locations.find((location) => location.id === deliveryLocationId) ?? null
    : null
  const deliveryFee = chosenLocation ? Number(chosenLocation.deliveryFee) : null
  const total = (Math.round(subtotal * 100) + (deliveryFee !== null && Number.isFinite(deliveryFee) ? Math.round(deliveryFee * 100) : 0)) / 100

  return (
    <div dir={dir} className={lang === 'ar' ? 'min-h-screen bg-background font-arabic' : 'min-h-screen bg-background font-sans'}>
      <CommandHeader title={ot(lang, 'newOrderTitle')} lang={lang} onLangToggle={onLangToggle} isOnline={isOnline} />

      <main className="mx-auto max-w-3xl space-y-4 px-4 py-6 pb-24">

        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          {submitError && (
            <div id="noi-submit-error" className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
              {submitError}
            </div>
          )}

          <div className="rounded-lg border bg-card p-4 space-y-3">
            <div>
              {!customer && (
                <>
                  <label htmlFor="noi-customerPhone" className="block text-sm font-medium text-gray-700 mb-1">
                    {ot(lang, 'newOrderCustomerPhone')}
                  </label>
                  <input
                    id="noi-customerPhone"
                    type="tel"
                    dir="ltr"
                    inputMode="tel"
                    autoComplete="off"
                    value={customerPhone}
                    onChange={(e) => handleCustomerPhoneChange(e.target.value)}
                    aria-describedby={customerError ? 'noi-customer-error' : phoneReadsAs ? 'noi-phone-reads-as' : undefined}
                    aria-invalid={!!customerError}
                    className={inputClass}
                  />

                  {phoneReadsAs && (
                    <p id="noi-phone-reads-as" className="mt-1 text-sm text-gray-700">
                      {ot(lang, 'newOrderPhoneReadsAs')} <span dir="ltr">{formatLebanesePhone(phoneReadsAs)}</span>
                    </p>
                  )}

                  {!isOnline && phoneDigits >= PHONE_SEARCH_MIN_DIGITS && (
                    <p id="noi-customer-offline" className="mt-1 text-sm text-gray-700">{ot(lang, 'newOrderSearchOffline')}</p>
                  )}

                  {customerSearching && (
                    <p id="noi-customer-searching" className="mt-1 text-sm text-gray-700" aria-live="polite">
                      {ot(lang, 'newOrderCustomerSearching')}
                    </p>
                  )}

                  {customerError && (
                    <p id="noi-customer-error" className="mt-1 text-sm text-red-700">
                      {customerError}
                    </p>
                  )}

                  {customerItems.length > 0 && (
                    <div className="mt-2 space-y-1">
                      <p className="text-sm font-medium text-gray-700">{ot(lang, 'newOrderCustomerPick')}</p>
                      {customerItems.map((found) => (
                        <button
                          key={found.id}
                          type="button"
                          className="noi-customer-option flex min-h-[44px] w-full items-center justify-between gap-2 rounded-md border px-3 py-2 text-start text-sm hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900"
                          onClick={() => pickCustomer(found)}
                        >
                          <span>{found.name ?? ot(lang, 'newOrderCustomerNoName')}</span>
                          {found.phone && <span className="shrink-0 text-gray-700" dir="ltr">{formatLebanesePhone(found.phone)}</span>}
                        </button>
                      ))}
                      {customerMore > 0 && (
                        <p id="noi-customer-more" className="text-sm text-gray-700">
                          {ot(lang, 'newOrderCustomerMore').replace('{count}', String(customerMore))}
                        </p>
                      )}
                    </div>
                  )}

                  {offerCreate && (
                    <div className="mt-2 space-y-2">
                      <p className="text-sm text-gray-700">{ot(lang, 'newOrderCustomerNone')}</p>
                      <label htmlFor="noi-customerName" className="block text-sm font-medium text-gray-700 mb-1">
                        {ot(lang, 'newOrderCustomerName')}
                      </label>
                      <input
                        id="noi-customerName"
                        value={customerName}
                        onChange={(e) => setCustomerName(e.target.value)}
                        className={inputClass}
                      />
                      <Button type="button" id="noi-customerCreate" variant="secondary" onClick={handleCustomerCreate}>
                        {ot(lang, 'newOrderCreateCustomer')}
                      </Button>
                    </div>
                  )}
                </>
              )}

              {customer && (
                <div className="flex items-center justify-between rounded-md border px-3 py-2">
                  <span className="text-sm">{customer.name ?? ot(lang, 'newOrderCustomerNoName')}</span>
                  <Button type="button" id="noi-customerChangeBtn" variant="outline" size="sm" onClick={handleCustomerChange}>
                    {ot(lang, 'newOrderCustomerChange')}
                  </Button>
                </div>
              )}

              {fieldErrors.customerId && (
                <p id="noi-customerId-error" className="mt-1 text-sm text-red-700">
                  {fieldErrors.customerId}
                </p>
              )}
            </div>
          </div>

          <div className="rounded-lg border bg-card p-4 space-y-3">
            <h2 className="text-sm font-semibold">{ot(lang, 'newOrderItemsTitle')}</h2>
            {fieldErrors.items && (
              <p id="noi-items-error" className="text-sm text-red-700">
                {fieldErrors.items}
              </p>
            )}
            <div>
              <label htmlFor="noi-product-search" className="block text-sm font-medium text-gray-700 mb-1">
                {ot(lang, 'newOrderProductSearch')}
              </label>
              <input
                id="noi-product-search"
                ref={productBoxRef}
                type="search"
                dir="auto"
                autoComplete="off"
                value={productQuery}
                onChange={(e) => handleProductQueryChange(e.target.value)}
                aria-describedby={fieldErrors.items ? 'noi-items-error' : undefined}
                aria-invalid={!!fieldErrors.items}
                className={inputClass}
              />

              {productQueryTrimmed.length > 0 && productQueryTrimmed.length < PRODUCT_SEARCH_MIN_CHARS && (
                <p id="noi-product-minchars" className="mt-1 text-sm text-gray-700">{ot(lang, 'newOrderProductMinChars')}</p>
              )}

              {!isOnline && productQueryTrimmed.length >= PRODUCT_SEARCH_MIN_CHARS && (
                <p id="noi-product-offline" className="mt-1 text-sm text-gray-700">{ot(lang, 'newOrderSearchOffline')}</p>
              )}

              {productSearching && (
                <p id="noi-product-searching" className="mt-1 text-sm text-gray-700" aria-live="polite">
                  {ot(lang, 'newOrderProductSearching')}
                </p>
              )}

              {productError && (
                <p id="noi-product-error" className="mt-1 text-sm text-red-700">
                  {productError}
                </p>
              )}

              {productResults && productResults.length === 0 && (
                <p className="mt-1 text-sm text-gray-700">{ot(lang, 'newOrderProductNone')}</p>
              )}

              {productResults && productResults.length > 0 && (
                <div className="mt-1 space-y-1">
                  {productResults.map((product) => (
                    <button
                      key={product.id}
                      type="button"
                      className="noi-product-option flex min-h-[44px] w-full items-center justify-between gap-2 rounded-md border px-3 py-2 text-start text-sm hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900"
                      onClick={() => pickProduct(product)}
                    >
                      <span>{productName(product, lang)}</span>
                      <span className="shrink-0 text-gray-700" dir="ltr">{money(Number(product.price))}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {items.length === 0 && (
              <p id="noi-no-lines" className="text-sm text-gray-700">{ot(lang, 'newOrderNoLines')}</p>
            )}

            {items.map((item, i) => (
              <div key={item.key} className="noi-line flex flex-wrap items-end gap-2 border-t pt-3">
                <div className="flex-1 min-w-[140px]">
                  <p id={`noi-item-${i}-name`} className="text-sm font-medium">
                    {productName(item.product, lang)}
                    <span className="ms-2 text-gray-700" dir="ltr">{money(Number(item.product.price))} {ot(lang, 'each')}</span>
                  </p>

                  {item.product.variants.length > 1 && (
                    <div className="mt-2">
                      <label htmlFor={`noi-item-${i}-variant`} className="block text-sm font-medium text-gray-700 mb-1">
                        {ot(lang, 'newOrderVariant')}
                      </label>
                      <select
                        id={`noi-item-${i}-variant`}
                        value={item.variantId}
                        onChange={(e) => updateItem(i, { variantId: e.target.value })}
                        aria-describedby={!item.variantId ? `noi-item-${i}-variant-reason` : undefined}
                        aria-invalid={!item.variantId && !!fieldErrors[`item-${i}-variant`]}
                        className={inputClass}
                      >
                        <option value="" disabled>{ot(lang, 'newOrderSelectPlaceholder')}</option>
                        {item.product.variants.map((variant) => (
                          <option key={variant.id} value={variant.id}>
                            {(lang === 'ar' ? variant.nameAr : variant.nameEn) ?? variant.id}
                          </option>
                        ))}
                      </select>
                      {!item.variantId && (
                        <p
                          id={`noi-item-${i}-variant-reason`}
                          className={fieldErrors[`item-${i}-variant`] ? 'mt-1 text-sm text-red-700' : 'mt-1 text-sm text-gray-700'}
                        >
                          {ot(lang, 'newOrderVariantRequired')}
                        </p>
                      )}
                    </div>
                  )}
                </div>
                <div className="w-24">
                  <label htmlFor={`noi-item-${i}-quantity`} className="block text-sm font-medium text-gray-700 mb-1">
                    {ot(lang, 'newOrderQuantity')}
                  </label>
                  <input
                    id={`noi-item-${i}-quantity`}
                    type="number"
                    dir="ltr"
                    min={1}
                    max={1000}
                    value={item.quantity}
                    onChange={(e) => updateItem(i, { quantity: e.target.value })}
                    aria-describedby={fieldErrors[`item-${i}-quantity`] ? `noi-item-${i}-quantity-error` : undefined}
                    aria-invalid={!!fieldErrors[`item-${i}-quantity`]}
                    className={inputClass}
                  />
                  {fieldErrors[`item-${i}-quantity`] && (
                    <p id={`noi-item-${i}-quantity-error`} className="mt-1 text-sm text-red-700">
                      {fieldErrors[`item-${i}-quantity`]}
                    </p>
                  )}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="min-h-[44px]"
                  id={`noi-item-${i}-remove`}
                  onClick={() => removeItem(i)}
                >
                  {ot(lang, 'newOrderRemoveItem')}
                </Button>
              </div>
            ))}
          </div>

          {/* Rook #66451: on a phone the card sticks to the bottom while lines are edited, above Create.
              Rook #66575 (Dee): it sits right after the items, above the notes and channel block. */}
          <section
            id="noi-totals"
            aria-labelledby="noi-totals-title"
            className="sticky bottom-0 z-10 rounded-lg border bg-card p-4 space-y-2 shadow-md md:static md:shadow-none"
            aria-live="polite"
          >
            <h2 id="noi-totals-title" className="text-sm font-semibold">{ot(lang, 'newOrderTotalTitle')}</h2>
            <div className="flex justify-between text-sm">
              <span>{ot(lang, 'newOrderLinesSubtotal')}</span>
              <span id="noi-lines-subtotal" dir="ltr">{money(subtotal)}</span>
            </div>
            {fulfillmentMethod === 'delivery' && (
              <div id="noi-delivery-fee" className="flex justify-between gap-2 text-sm">
                <span>{ot(lang, 'newOrderDeliveryFee')}</span>
                {deliveryFee !== null
                  ? <span dir="ltr">{money(deliveryFee)}</span>
                  : <span className="text-gray-700">{ot(lang, 'newOrderDeliveryFeePending')}</span>}
              </div>
            )}
            <div className="flex justify-between border-t pt-2 text-base font-semibold">
              <span>{ot(lang, 'newOrderTotal')}</span>
              <span id="noi-total" dir="ltr">{money(total)}</span>
            </div>
            <p className="text-xs text-gray-700">{ot(lang, 'newOrderTotalNote')}</p>
          </section>

          {/* R489 (Rook #66589, #66697): the block keeps notes, channel, the order type (SALE or
              COMP) and the fulfilment method (with its delivery fields); source order id,
              currency and FX rate id are gone. */}
          <div id="noi-order-block" className="rounded-lg border bg-card p-4 space-y-3">
            <div>
              <label htmlFor="noi-notes" className="block text-sm font-medium text-gray-700 mb-1">
                {ot(lang, 'newOrderNotes')}
              </label>
              <textarea
                id="noi-notes"
                value={notes}
                maxLength={2000}
                onChange={(e) => setNotes(e.target.value)}
                className={inputClass}
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="noi-channel" className="block text-sm font-medium text-gray-700 mb-1">
                  {ot(lang, 'newOrderChannel')}
                </label>
                <select
                  id="noi-channel"
                  value={channel}
                  onChange={(e) => setChannel(e.target.value as typeof channel)}
                  aria-describedby={fieldErrors.channel ? 'noi-channel-error' : undefined}
                  aria-invalid={!!fieldErrors.channel}
                  className={inputClass}
                >
                  <option value="">{ot(lang, 'newOrderSelectPlaceholder')}</option>
                  <option value="phone">{ot(lang, 'newOrderChannelPhone')}</option>
                  <option value="whatsapp">{ot(lang, 'newOrderChannelWhatsapp')}</option>
                  <option value="counter">{ot(lang, 'newOrderChannelCounter')}</option>
                  <option value="shop">{ot(lang, 'newOrderChannelShop')}</option>
                  <option value="toters">{ot(lang, 'newOrderChannelToters')}</option>
                </select>
                {fieldErrors.channel && (
                  <p id="noi-channel-error" className="mt-1 text-sm text-red-700">
                    {fieldErrors.channel}
                  </p>
                )}
              </div>
              <div>
                <label htmlFor="noi-orderType" className="block text-sm font-medium text-gray-700 mb-1">
                  {ot(lang, 'newOrderOrderType')}
                </label>
                <select
                  id="noi-orderType"
                  value={orderType}
                  onChange={(e) => setOrderType(e.target.value === 'COMP' ? 'COMP' : 'SALE')}
                  className={inputClass}
                >
                  <option value="SALE">SALE</option>
                  <option value="COMP">COMP</option>
                </select>
              </div>
              <div>
                <label htmlFor="noi-fulfillmentMethod" className="block text-sm font-medium text-gray-700 mb-1">
                  {ot(lang, 'newOrderFulfillmentMethod')}
                </label>
                <select
                  id="noi-fulfillmentMethod"
                  value={fulfillmentMethod}
                  onChange={(e) => handleFulfillmentChange(e.target.value as typeof fulfillmentMethod)}
                  aria-describedby={fieldErrors.fulfillmentMethod ? 'noi-fulfillmentMethod-error' : undefined}
                  aria-invalid={!!fieldErrors.fulfillmentMethod}
                  className={inputClass}
                >
                  <option value="">{ot(lang, 'newOrderSelectPlaceholder')}</option>
                  <option value="delivery">{ot(lang, 'delivery')}</option>
                  <option value="pickup">{ot(lang, 'pickup')}</option>
                </select>
                {fieldErrors.fulfillmentMethod && (
                  <p id="noi-fulfillmentMethod-error" className="mt-1 text-sm text-red-700">
                    {fieldErrors.fulfillmentMethod}
                  </p>
                )}
              </div>
              {fulfillmentMethod === 'delivery' && (
                <>
                  <div>
                    <label htmlFor="noi-zone" className="block text-sm font-medium text-gray-700 mb-1">
                      {ot(lang, 'newOrderZone')}
                    </label>
                    <select
                      id="noi-zone"
                      value={deliveryZoneId}
                      onChange={(e) => handleZoneChange(e.target.value)}
                      aria-describedby={fieldErrors.zone ? 'noi-zone-error' : undefined}
                      aria-invalid={!!fieldErrors.zone}
                      className={inputClass}
                    >
                      <option value="">{ot(lang, 'newOrderSelectPlaceholder')}</option>
                      {(zones ?? []).map((zone) => (
                        <option key={zone.id} value={zone.id}>
                          {lang === 'ar' ? zone.nameAr : zone.nameEn}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="noi-location" className="block text-sm font-medium text-gray-700 mb-1">
                      {ot(lang, 'newOrderLocation')}
                    </label>
                    <select
                      id="noi-location"
                      value={deliveryLocationId}
                      onChange={(e) => setDeliveryLocationId(e.target.value)}
                      className={inputClass}
                    >
                      <option value="">{ot(lang, 'newOrderSelectPlaceholder')}</option>
                      {(zones ?? [])
                        .find((zone) => zone.id === deliveryZoneId)
                        ?.locations.map((location) => (
                          <option key={location.id} value={location.id}>
                            {lang === 'ar' ? location.cityNameAr : location.cityNameEn} — {location.deliveryFee}
                          </option>
                        ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="noi-street" className="block text-sm font-medium text-gray-700 mb-1">
                      {ot(lang, 'street')}
                    </label>
                    <input
                      id="noi-street"
                      value={streetName}
                      onChange={(e) => setStreetName(e.target.value)}
                      maxLength={500}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="noi-deliveryNotes" className="block text-sm font-medium text-gray-700 mb-1">
                      {ot(lang, 'deliveryNotes')}
                    </label>
                    <textarea
                      id="noi-deliveryNotes"
                      value={deliveryNotes}
                      onChange={(e) => setDeliveryNotes(e.target.value)}
                      maxLength={500}
                      rows={2}
                      className={inputClass}
                    />
                  </div>
                  {fieldErrors.zone && (
                    <p id="noi-zone-error" className="mt-1 text-sm text-red-700">
                      {fieldErrors.zone}
                    </p>
                  )}
                </>
              )}
            </div>
          </div>

          <Button id="noi-submit" type="submit" size="lg" className="min-h-[48px] w-full" disabled={submitting}>
            {submitting ? ot(lang, 'newOrderSubmitting') : ot(lang, 'newOrderSubmit')}
          </Button>
        </form>
      </main>
    </div>
  )
}
